export const EVENT_GOAL_METRICS = ['registrations', 'netRevenue', 'ticketsSold', 'addOnsSold', 'checkIns'] as const;

export type EventGoalMetric = (typeof EVENT_GOAL_METRICS)[number];

export type EventGoal = {
	metric: EventGoalMetric;
	target: number;
};

// The ceiling of event_goals.target, a pg integer. Enforced on the write path
// and in the dialog: without it a fat-fingered target (or one typed in cents)
// reaches the column and Postgres rejects the whole transaction with "integer
// out of range", which surfaces to the user as a generic failure toast. The
// JSONB setting this table replaced had no ceiling, so narrowing the column is
// what created the need for one.
export const MAX_EVENT_GOAL_TARGET = 2_147_483_647;

const METRIC_ORDER = new Map<string, number>(EVENT_GOAL_METRICS.map((metric, index) => [metric, index]));

export const isEventGoalMetric = (value: unknown): value is EventGoalMetric =>
	typeof value === 'string' && METRIC_ORDER.has(value);

// Targets are whole units — people, tickets, add-ons, check-ins, or dollars. A
// fractional goal has no meaning on any of those scales, and flooring here keeps
// the stored value and the rendered headline from disagreeing.
export const parseEventGoalTarget = (value: unknown): number | null => {
	const target = typeof value === 'string' ? Number(value) : value;
	if (typeof target !== 'number' || !Number.isFinite(target) || target <= 0) return null;
	return Math.floor(target);
};

export const sortEventGoals = (goals: EventGoal[]): EventGoal[] =>
	[...goals].sort((a, b) => (METRIC_ORDER.get(a.metric) ?? 0) - (METRIC_ORDER.get(b.metric) ?? 0));

// event_goals.deadline_at is null for every goal today, meaning "inherit the
// event's end date". Storing the event's end date on the row instead would
// strand each goal on a stale deadline the moment the event moves, with nothing
// in the UI to reveal the drift. Never read deadline_at without this.
export const resolveEventGoalDeadline = (
	deadlineAt: string | null | undefined,
	eventEndDate: string | null | undefined
): string | null => deadlineAt ?? eventEndDate ?? null;

export type EventGoalRow = {
	metric: string;
	target: unknown;
};

// Narrows event_goals rows to renderable goals. The metric column is a varchar
// rather than a pg enum, so a row can name a metric this build does not know
// about; dropping it beats painting a bar nobody can compute. The partial
// unique index already keeps one live row per metric, so the dedupe here is
// belt-and-braces for reads that include soft-deleted rows.
export function parseEventGoalRows(rows: readonly EventGoalRow[]): EventGoal[] {
	const byMetric = new Map<EventGoalMetric, EventGoal>();
	for (const row of rows) {
		const metric = row?.metric;
		if (!isEventGoalMetric(metric) || byMetric.has(metric)) continue;
		const target = parseEventGoalTarget(row.target);
		if (target == null) continue;
		byMetric.set(metric, { metric, target });
	}

	return sortEventGoals([...byMetric.values()]);
}
